import React, { useState, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { classNameWithModifiers as withMods } from 'helpers/class-name';
import { scrollToTop } from 'helpers/page-utils';
import { usePrevious } from 'helpers/react-hooks';
import HeaderFrame from './HeaderFrameContainer';
import { PageLayoutContext } from './Contexts';

import './ColumnLayout.scss';

function getNameIndex(focus, children) {
  const childrenArray = React.Children.toArray(children);
  const names = childrenArray.map(({ props: childProps }) => childProps.name).filter(Boolean);
  return Math.max(
    names.findIndex(name => name === focus),
    0
  );
}

// The displayRow prop accepts a function returning a non-column component to render above columns (used for banners).
// The row component will only display in the desktop view due to the way we handle children with individual headers.

const ColumnLayout = ({ children, className, displayRow, setColumnFocus, isDesktop, focus, dataCypress }) => {
  const containerRef = useRef(null);
  const [transitioning, setTransitioning] = useState(false);
  const [nameIndex, setNameIndex] = useState(getNameIndex);
  const [nextNameIndex, setNextNameIndex] = useState(null);
  const previousFocus = usePrevious(focus);

  const childrenArray = React.Children.toArray(children);
  const childPropsByName = childrenArray.reduce((acc, child) => ({ ...acc, [child.props.name]: child.props }), {});

  useLayoutEffect(() => {
    const currentNameIndex = getNameIndex(focus, children);
    if (currentNameIndex >= 0 && nameIndex !== currentNameIndex && !isDesktop) {
      setTransitioning(true);
    }

    if (previousFocus !== focus) {
      const prevProps = previousFocus ? childPropsByName[previousFocus] : {};

      if (prevProps && prevProps.onFocusLeave) {
        prevProps.onFocusLeave();
      }
    }
  }, [focus]);

  useLayoutEffect(() => {
    // Force browser to recalculate sticky positions
    // eslint-disable-next-line no-self-assign
    document.documentElement.scrollTop = document.documentElement.scrollTop;
    setNextNameIndex(getNameIndex(focus, children));
  }, [transitioning]);

  const wrapperChildren = (
    <PageLayoutContext.Consumer>
      {({ layoutInfo: { pageLayout } }) => (
        <div
          ref={containerRef}
          className={withMods(
            'column-layout__container',
            `column-layout__container--visible-column-${transitioning ? nextNameIndex : nameIndex}`,
            `column-layout__container--${pageLayout}`
          )}
          onTransitionEnd={event => {
            // Ensure that we're catching only transitions for this element, and not its children
            if (containerRef.current === event.target) {
              setNameIndex(nextNameIndex);
              setTransitioning(false);

              const nextProps = focus ? childPropsByName[focus] : {};
              if (nextProps.onFocusEnter) {
                nextProps.onFocusEnter();
              } else {
                scrollToTop();
              }
            }
          }}>
          {React.Children.map(childrenArray, (child, index) => {
            return React.cloneElement(child, {
              className: classNames(index === nameIndex && 'column-layout__column--active', child.props.className),
              back: index > 0 ? setColumnFocus.bind(null, childrenArray[index - 1].props.name) : null,
              forward:
                index < childrenArray.length - 1
                  ? setColumnFocus.bind(null, childrenArray[index + 1].props.name)
                  : null,
              type: pageLayout === 'public' ? pageLayout : child.props.type,
              index,
            });
          })}
        </div>
      )}
    </PageLayoutContext.Consumer>
  );

  return (
    <PageLayoutContext.Consumer>
      {({ layoutInfo: { isFixedPage } }) => (
        <div
          className={withMods(
            'column-layout',
            {
              transitioning,
              isFixedPage,
            },
            className
          )}
          data-cypress={dataCypress}>
          {/* in "mobile" mode, each Column gets its own HeaderFrame,
             in "desktop" mode, there's just 1 HeaderFrame for the page. */}
          {isDesktop ? (
            <HeaderFrame>
              {displayRow ? <div className="column-layout__display-row">{displayRow}</div> : null}
              {wrapperChildren}
            </HeaderFrame>
          ) : (
            wrapperChildren
          )}
        </div>
      )}
    </PageLayoutContext.Consumer>
  );
};

ColumnLayout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  dataCypress: PropTypes.string,
  displayRow: PropTypes.shape({}),
  focus: PropTypes.string,
  setColumnFocus: PropTypes.func,
  isDesktop: PropTypes.bool,
};

export default ColumnLayout;
